<template>
  <!-- eslint-disable-next-line vue/no-v-html -->
  <div
    class="cms-content"
    :class="`cms-content--${color}`"
    v-html="processedContent"
  />
</template>

<script setup lang="ts">
import { computed } from "vue";

import { injectContentInterpolator } from "~/shared/composables/useContentParams.ts";

const props = withDefaults(
  defineProps<{
    color?: "gray" | "primary" | "white";
    content: string | null;
  }>(),
  { color: "gray" },
);

const interpolate = injectContentInterpolator();

const processedContent = computed(
  () => props.content && interpolate(props.content),
);
</script>

<style lang="postcss">
.cms-content {
  :is(h1, h2, h3, h4, h5) {
    @apply block font-secondary;

    &::first-letter {
      @apply uppercase;
    }
  }

  h1 {
    @apply mb-3 text-4xl font-extrabold;

    @screen 800 {
      @apply text-5xl;
    }

    @screen 1000 {
      @apply text-6xl;
    }
  }

  h2 {
    @apply mb-2 text-2xl font-bold;

    @screen 800 {
      @apply text-3xl;
    }
  }

  h3 {
    @apply mb-2 text-lg font-bold;

    @screen 600 {
      @apply text-xl;
    }
  }

  h4 {
    @apply mb-1 text-base font-bold;

    @screen 600 {
      @apply text-lg;
    }
  }

  h5 {
    @apply mb-1 text-sm font-bold;

    @screen 600 {
      @apply text-base;
    }

    @screen 800 {
      @apply text-lg;
    }
  }

  &--gray {
    p,
    :is(h1, h2, h3, h4, h5) {
      @apply text-slate-700;
    }
  }

  &--primary :is(h1, h2, h3, h4, h5) {
    @apply text-primary-700;
  }

  &--white {
    p,
    :is(h1, h2, h3, h4, h5) {
      @apply text-white;
    }
  }

  p {
    @apply mb-4 min-h-6;

    &:last-child {
      @apply mb-0;
    }
  }

  a {
    @apply cursor-pointer text-primary-700 underline transition-text;

    &:hover {
      @apply text-primary-500;
    }
  }

  ol,
  ul {
    @apply my-4 pl-5;

    li + li {
      @apply mt-2;
    }

    li p {
      @apply mb-0;
    }
  }

  ul {
    @apply list-disc;
  }

  ol {
    @apply list-decimal;
  }

  blockquote {
    @apply relative mb-4 overflow-hidden rounded-bl-2xl rounded-tr-2xl bg-slate-100 px-6 py-6 font-normal text-slate-700;

    &::before {
      content: "";
      @apply absolute left-0 top-0 h-full w-1.5 bg-primary-700;
    }

    p:last-child {
      @apply mb-0;
    }

    :is(h1, h2, h3, h4, h5) {
      @apply text-primary-700;
    }
  }

  table {
    @apply mb-4 block w-full border-separate border-spacing-0 overflow-x-auto rounded-xl border border-slate-200 text-sm;

    @screen 800 {
      @apply table;
    }

    tbody {
      tr:first-child {
        @apply bg-primary-700;

        p {
          @apply text-white;
        }

        th {
          @apply border-0 px-4 py-3 text-left font-normal;
        }

        th:first-child {
          @apply rounded-tl-xl;
        }

        th:last-child {
          @apply rounded-tr-xl;
        }
      }

      tr:not(:first-child) {
        td {
          @apply border-b border-slate-200 px-4 py-4 text-slate-700;
        }
      }

      tr:last-child {
        td {
          @apply border-b-0;
        }

        td:first-child {
          @apply rounded-bl-xl;
        }

        td:last-child {
          @apply rounded-br-xl;
        }
      }

      tr:nth-child(even) {
        @apply bg-primary-50;
      }

      td p,
      th p {
        @apply mb-0 min-h-0;
      }
    }
  }
}
</style>
